import { groupSums } from './aggregate';
import type { PathmaticsData } from './pathmaticsData.schema';

export type DimensionKind = 'channels' | 'publishers';

/** Matches the bucket name the backend already writes for a capped publisher tail. */
// eslint-disable-next-line i18next/no-literal-string -- wire value written by the API, not copy
export const OTHER_DIMENSION = 'Other';

export const TOP_DIMENSIONS = 6;

export interface DimensionRow {
  advertiser: string;
  dimension: string;
  month: string;
  spend: number;
  impressions: number;
}

export interface MarketDimensionRow {
  dimension: string;
  month: string;
  spend: number;
  impressions: number;
}

export function advertiserDimensionRows(data: PathmaticsData, kind: DimensionKind): DimensionRow[] {
  if (kind === 'channels') {
    return data.mediaLandscape.byChannel.map(({ channel, ...rest }) => ({
      ...rest,
      dimension: channel,
    }));
  }
  return data.mediaLandscape.byPublisher.map(({ publisher, ...rest }) => ({
    ...rest,
    dimension: publisher,
  }));
}

export function marketDimensionRows(
  data: PathmaticsData,
  kind: DimensionKind,
): MarketDimensionRow[] {
  if (kind === 'channels') {
    return data.mediaLandscape.marketTotals.byChannel.map(({ channel, ...rest }) => ({
      ...rest,
      dimension: channel,
    }));
  }
  return data.mediaLandscape.marketTotals.byPublisher.map(({ publisher, ...rest }) => ({
    ...rest,
    dimension: publisher,
  }));
}

/**
 * Highest-spend dimensions in the supplied (already filtered) rows, capped at six. The
 * backend's own `Other` tail is excluded so it cannot take a named column from a real
 * dimension; `bucketRows` folds it back in.
 */
export function topDimensions(rows: DimensionRow[]): string[] {
  const sums = groupSums(
    rows.filter((r) => r.dimension !== OTHER_DIMENSION),
    (r) => r.dimension,
  );
  return Array.from(sums.entries())
    .sort(([nameA, a], [nameB, b]) => b.spend - a.spend || nameA.localeCompare(nameB))
    .slice(0, TOP_DIMENSIONS)
    .map(([name]) => name);
}

export function bucketRows<T extends { dimension: string }>(rows: T[], top: string[]): T[] {
  const named = new Set(top);
  return rows.map((r) => (named.has(r.dimension) ? r : { ...r, dimension: OTHER_DIMENSION }));
}

/**
 * The columns a table or bar chart should actually render: the top six, plus `Other` appended
 * when anything falls outside them (the tail beyond six, or the backend's own pre-bucketed
 * `Other` rows). Rows total to `topDimensions`' six only when nothing overflows, so a caller
 * that renders just those six would silently under-sum; appending `Other` here is what keeps
 * that sum honest. Omitted when nothing overflows, so a small file stays clean.
 *
 * Columns are still ranked on the advertiser rows alone, but a caller that also renders a
 * market row must pass those rows: the market spends in dimensions the current selection does
 * not, and without them that tail has no column to land in while still counting towards the
 * market total.
 */
export function displayDimensions(
  rows: DimensionRow[],
  marketRows: Array<{ dimension: string; spend: number; impressions: number }> = [],
): string[] {
  const top = topDimensions(rows);
  const named = new Set(top);
  // Every metric shares these columns, so a dimension carrying impressions but no
  // spend still needs one: ranking drops it, and without `Other` its impressions
  // count towards the total while no cell holds them.
  const overflows = (row: { dimension: string; spend: number; impressions: number }) =>
    (row.spend > 0 || row.impressions > 0) && !named.has(row.dimension);
  return rows.some(overflows) || marketRows.some(overflows) ? [...top, OTHER_DIMENSION] : top;
}
